import { useQuery } from "@tanstack/react-query"
import { AlertCircle, Package } from "lucide-react"
import { useRef, useState } from "react"
import { Link } from "react-router"
import { api, ErrorApi } from "@/api/cliente"
import { BadgeEstatus, type Estatus } from "@/componentes/expediente/BadgeEstatus"
import { Exportar } from "@/componentes/exportar/Exportar"
import { Cabecera, Cuerpo, Fila, Tabla, Td, Th } from "@/componentes/ui/Tabla"
import { comoFecha } from "@/lib/fechas"

/**
 * Bandeja de expedientes.
 *
 * Los once estatus son **literalmente los del 2.0**, tomados de su migración. No se
 * renombran ni se reordenan: la gente que los usa lleva años con estos nombres, y
 * «mejorarlos» aquí obligaría a traducir mentalmente entre los dos sistemas
 * mientras conviven.
 */

/** En su orden operativo, que no es el alfabético: es el del expediente real. */
const ESTATUS: Estatus[] = [
  "POR_LLEGAR",
  "POR_REGISTRO",
  "RECONOCIMIENTO",
  "DETENIDO",
  "VALIDADO",
  "POR_DESPACHO",
  "PENDIENTE_APROBACION_DESPACHO",
  "DESPACHADO",
  "LIQUIDADO",
  "POR_COBRAR",
  "COBRADO",
]

interface FilaExpediente {
  id: number
  numero: string
  cliente_id: number
  aduana_id: number
  estado: "ABIERTO" | "CERRADO"
  estatus_operativo: Estatus
  fecha_apertura: string
  fecha_cierre: string | null
  cotizacion_id: number | null
}

interface Pagina {
  total: number
  limit: number
  offset: number
  items: FilaExpediente[]
}

const POR_PAGINA = 25

export function Expedientes() {
  const [offset, setOffset] = useState(0)
  const [estatus, setEstatus] = useState<Estatus | "">("")

  const parametros = new URLSearchParams({ limit: String(POR_PAGINA), offset: String(offset) })
  if (estatus) parametros.set("estatus", estatus)

  const consulta = useQuery({
    queryKey: ["expedientes", { offset, estatus }],
    queryFn: () => api.get<Pagina>(`/api/v1/expedientes?${parametros}`),
  })

  const pagina = consulta.data
  const hasta = pagina ? Math.min(pagina.offset + pagina.limit, pagina.total) : 0
  const vista = useRef<HTMLDivElement | null>(null)
  const subtitulo = [
    estatus ? `Estatus: ${estatus.replaceAll("_", " ")}` : "Todos los estatus",
    pagina && pagina.total > 0 ? `${pagina.offset + 1}–${hasta} de ${pagina.total}` : null,
  ]
    .filter(Boolean)
    .join(" · ")

  return (
    <div ref={vista}>
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Expedientes</h1>
          <p className="mt-1 text-sm" style={{ color: "var(--fg-muted)" }}>
            {pagina ? `${pagina.total} en total` : " "}
          </p>
        </div>
        <Exportar titulo="Expedientes" subtitulo={subtitulo} contenido={vista} />
      </div>

      <div className="mb-4">
        <label htmlFor="filtro-estatus" className="mr-2 text-sm">
          Estatus operativo
        </label>
        <select
          id="filtro-estatus"
          value={estatus}
          onChange={(e) => {
            setEstatus(e.target.value as Estatus | "")
            setOffset(0)
          }}
          className="h-9 rounded-[var(--radius-control)] border px-2 text-sm"
          style={{
            borderColor: "var(--border)",
            backgroundColor: "var(--surface)",
            color: "var(--fg)",
          }}
        >
          <option value="">Todos</option>
          {ESTATUS.map((e) => (
            <option key={e} value={e}>
              {e.replaceAll("_", " ")}
            </option>
          ))}
        </select>
      </div>

      {consulta.isPending && (
        <p className="py-12 text-center text-sm" style={{ color: "var(--fg-muted)" }}>
          Cargando expedientes…
        </p>
      )}

      {consulta.isError && (
        <div
          role="alert"
          className="flex items-start gap-3 superficie p-4"
          style={{ borderColor: "var(--destructive)", color: "var(--destructive)" }}
        >
          <AlertCircle size={20} aria-hidden="true" />
          <div>
            <p className="font-medium">No se pudieron cargar los expedientes</p>
            <p className="mt-1 text-sm">
              {consulta.error instanceof ErrorApi
                ? consulta.error.message
                : "Error inesperado de red"}
            </p>
          </div>
        </div>
      )}

      {pagina && pagina.items.length === 0 && (
        <div
          className="flex flex-col items-center gap-3 superficie py-16"
          style={{ color: "var(--fg-muted)" }}
        >
          <Package size={24} aria-hidden="true" />
          <p className="text-sm">
            {estatus
              ? `No hay expedientes en ${estatus.replaceAll("_", " ")}`
              : "Todavía no hay expedientes"}
          </p>
        </div>
      )}

      {pagina && pagina.items.length > 0 && (
        <Tabla etiqueta="Expedientes">
          <Cabecera>
            <Th>Número</Th>
            <Th>Estatus operativo</Th>
            <Th>Estado</Th>
            <Th derecha>Apertura</Th>
          </Cabecera>
          <Cuerpo>
            {pagina.items.map((fila) => (
              <Fila key={fila.id}>
                <Td>
                  <Link to={`/expedientes/${fila.id}`} className="font-medium underline">
                    {fila.numero}
                  </Link>
                </Td>
                <Td>
                  <BadgeEstatus estatus={fila.estatus_operativo} />
                </Td>
                <Td style={{ color: "var(--fg-muted)" }}>{fila.estado}</Td>
                <Td derecha numero>
                  {comoFecha(fila.fecha_apertura)}
                </Td>
              </Fila>
            ))}
          </Cuerpo>
        </Tabla>
      )}

      {pagina && pagina.total > pagina.limit && (
        <div className="mt-4 flex items-center justify-between text-sm">
          <span className="tabular" style={{ color: "var(--fg-muted)" }}>
            {pagina.offset + 1}–{hasta} de {pagina.total}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - POR_PAGINA))}
              className="h-9 rounded-[var(--radius-control)] border px-3 disabled:opacity-40"
              style={{ borderColor: "var(--border)" }}
            >
              Anterior
            </button>
            <button
              type="button"
              disabled={hasta >= pagina.total}
              onClick={() => setOffset(offset + POR_PAGINA)}
              className="h-9 rounded-[var(--radius-control)] border px-3 disabled:opacity-40"
              style={{ borderColor: "var(--border)" }}
            >
              Siguiente
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
